import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { format } from "date-fns";
import { 
  Search, 
  CalendarIcon, 
  Edit2, 
  Trash2, 
  Filter, 
  X,
  Plus,
  RefreshCw,
  Phone,
  CheckCircle,
  XCircle,
  ShoppingCart,
  MoreHorizontal,
  Headphones,
  Download,
  FileSpreadsheet,
  Pencil,
  Check
} from "lucide-react";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: "Pending", label: "Pending", color: "bg-gray-100 text-gray-800" },
  { value: "Tinawagan", label: "Tinawagan", color: "bg-blue-100 text-blue-800" },
  { value: "Nag Re-order", label: "Nag Re-order", color: "bg-green-100 text-green-800" },
  { value: "Hindi Na-contact", label: "Hindi Na-contact", color: "bg-yellow-100 text-yellow-800" },
  { value: "Ayaw Mag Re-order", label: "Ayaw Mag Re-order", color: "bg-red-100 text-red-800" },
  { value: "After-Sales", label: "After-Sales", color: "bg-purple-100 text-purple-800" },
];

const ISSUE_TYPES = ["Wrong Item", "Defective", "Missing", "Incomplete", "Other"];
const ACTIONS = ["Replacement", "Reship", "Refund"];

// Platform filter options
const PLATFORM_FILTER_OPTIONS = [
  { value: "all", label: "All Platforms" },
  { value: "FB/J&T-QC", label: "FB/J&T-QC" },
  { value: "FB/J&T-PQ", label: "FB/J&T-PQ" },
  { value: "Shopee", label: "Shopee" },
  { value: "TikTok", label: "TikTok" },
  { value: "Lazada", label: "Lazada" },
];

export default function RTSRecords() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [filters, setFilters] = useState({
    staff: "",
    reason: "",
    startDate: null,
    endDate: null,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showReorderDialog, setShowReorderDialog] = useState(false);
  const [showAfterSalesDialog, setShowAfterSalesDialog] = useState(false);
  const [afterSalesForm, setAfterSalesForm] = useState({
    issue_type: "",
    issue_description: "",
    action_taken: "",
    new_tracking_number: "",
    staff_in_charge: ""
  });
  const [reorderData, setReorderData] = useState({
    reorder_items: [{ item_name: "", quantity: 1 }],
    reorder_notes: "",
    call_notes: ""
  });
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Bulk Update Platform State
  const [showBulkUpdateDialog, setShowBulkUpdateDialog] = useState(false);
  const [bulkUpdateStaff, setBulkUpdateStaff] = useState("");
  const [bulkUpdatePlatform, setBulkUpdatePlatform] = useState("FB/J&T-QC");
  const [bulkUpdating, setBulkUpdating] = useState(false);

  // Bulk Update Platform Handler
  const handleBulkUpdatePlatform = async () => {
    if (!bulkUpdateStaff.trim()) {
      toast.error("Enter staff name to filter");
      return;
    }
    
    setBulkUpdating(true);
    try {
      const response = await axios.post(`${API}/rts/bulk-update-platform`, null, {
        params: {
          staff_name: bulkUpdateStaff,
          new_platform: bulkUpdatePlatform
        }
      });
      
      toast.success(`✅ ${response.data.modified} records updated to ${bulkUpdatePlatform}`);
      setShowBulkUpdateDialog(false);
      setBulkUpdateStaff("");
      fetchRecords(); // Refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update records");
    } finally {
      setBulkUpdating(false);
    }
  };

  // Export to Excel function
  const exportToExcel = async () => {
    if (records.length === 0) {
      toast.error("No records to export");
      return;
    }
    
    setExporting(true);
    
    try {
      // Create CSV content with complete details
      const headers = [
        "Date Created",
        "Tracking Number",
        "Date Ordered",
        "Customer Name",
        "Contact",
        "Address",
        "Platform",
        "Branch",
        "Items (All)",
        "Reason",
        "Cause Classification",
        "Ordered By",
        "Upselled By",
        "Preparer",
        "Status",
        "Call Date",
        "Call Notes",
        "Reorder Date",
        "Reorder Items",
        "Reorder Notes",
        "Notes"
      ];
      
      const rows = records.map(record => {
        // Format all items as a single string
        const itemsStr = record.orders?.map(o => `${o.item_name} x${o.quantity}`).join("; ") || "";
        // Format reorder items
        const reorderItemsStr = record.reorder_items?.map(o => `${o.item_name} x${o.quantity}`).join("; ") || "";
        // Get platform display
        const platformDisplay = record.platform || "";
        const branchDisplay = record.branch || "";
        
        return [
          record.created_at ? new Date(record.created_at).toLocaleString() : "",
          record.tracking_number || "",
          record.date_ordered || "",
          record.customer_name || "",
          record.contact_number || "",
          (record.address || "").replace(/[\n\r]/g, " "),
          platformDisplay,
          branchDisplay,
          itemsStr,
          record.reason || "",
          record.cause_classification || "",
          record.ordered_by || "",
          record.upselled_by || "",
          record.preparer || "",
          record.status || "Pending",
          record.call_date || "",
          (record.call_notes || "").replace(/[\n\r]/g, " "),
          record.reorder_date || "",
          reorderItemsStr,
          (record.reorder_notes || "").replace(/[\n\r]/g, " "),
          (record.notes || "").replace(/[\n\r]/g, " ")
        ];
      });
      
      // Escape CSV values
      const escapeCSV = (value) => {
        if (value === null || value === undefined) return "";
        const str = String(value);
        if (str.includes(",") || str.includes('"') || str.includes("\n")) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };
      
      const csvContent = [
        headers.map(escapeCSV).join(","),
        ...rows.map(row => row.map(escapeCSV).join(","))
      ].join("\n");
      
      // Create blob and download
      const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      
      // Generate filename with date
      const today = new Date().toISOString().split("T")[0];
      link.download = `RTS_Records_${today}.csv`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`✅ Exported ${records.length} records to Excel!`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export records");
    } finally {
      setExporting(false);
    }
  };

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (filters.staff) params.append("staff", filters.staff);
      if (filters.reason) params.append("reason", filters.reason);
      if (filters.startDate) params.append("start_date", format(filters.startDate, "yyyy-MM-dd"));
      if (filters.endDate) params.append("end_date", format(filters.endDate, "yyyy-MM-dd"));
      if (platformFilter && platformFilter !== "all") params.append("platform", platformFilter);

      const response = await axios.get(`${API}/rts?${params.toString()}`);
      setRecords(response.data);
    } catch (error) {
      console.error("Error fetching records:", error);
      toast.error("Failed to fetch RTS records");
    } finally {
      setLoading(false);
    }
  }, [search, filters, platformFilter]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this RTS entry?")) return;
    
    try {
      await axios.delete(`${API}/rts/${id}`);
      toast.success("RTS entry deleted successfully");
      fetchRecords();
    } catch (error) {
      console.error("Error deleting record:", error);
      toast.error("Failed to delete RTS entry");
    }
  };

  const handleEdit = (record) => {
    setEditingRecord({ ...record });
    setShowEditDialog(true);
  };

  const handleUpdateRecord = async () => {
    if (!editingRecord) return;
    
    try {
      await axios.put(`${API}/rts/${editingRecord.id}`, editingRecord);
      toast.success("RTS entry updated successfully");
      setShowEditDialog(false);
      setEditingRecord(null);
      fetchRecords();
    } catch (error) {
      console.error("Error updating record:", error);
      toast.error("Failed to update RTS entry");
    }
  };

  const handleStatusChange = async (record, newStatus) => {
    try {
      const updateData = { 
        status: newStatus,
        call_date: format(new Date(), "yyyy-MM-dd")
      };
      
      await axios.put(`${API}/rts/${record.id}`, updateData);
      toast.success(`Status updated to "${newStatus}"`);
      fetchRecords();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const openReorderDialog = (record) => {
    setEditingRecord(record);
    setReorderData({
      reorder_items: [{ item_name: "", quantity: 1 }],
      reorder_notes: "",
      call_notes: record.call_notes || ""
    });
    setShowReorderDialog(true);
  };

  const openAfterSalesDialog = (record) => {
    setEditingRecord(record);
    setAfterSalesForm({
      issue_type: "",
      issue_description: "",
      action_taken: "",
      new_tracking_number: "",
      staff_in_charge: ""
    });
    setShowAfterSalesDialog(true);
  };

  const handleCreateAfterSales = async () => {
    if (!editingRecord) return;
    
    if (!afterSalesForm.issue_type || !afterSalesForm.action_taken || !afterSalesForm.staff_in_charge) {
      toast.error("Please fill in Issue Type, Action Taken, and Staff In Charge");
      return;
    }

    try {
      // Get first item from orders
      const firstItem = editingRecord.orders?.[0]?.item_name || "N/A";
      
      const afterSalesData = {
        original_rts_id: editingRecord.id,
        original_tracking_number: editingRecord.tracking_number,
        customer_name: editingRecord.customer_name,
        contact_number: editingRecord.contact_number || "",
        item: firstItem,
        issue_type: afterSalesForm.issue_type,
        issue_description: afterSalesForm.issue_description,
        action_taken: afterSalesForm.action_taken,
        new_tracking_number: afterSalesForm.new_tracking_number,
        staff_in_charge: afterSalesForm.staff_in_charge,
        status: "Pending"
      };
      
      await axios.post(`${API}/after-sales`, afterSalesData);
      
      // Update RTS status to After-Sales
      await axios.put(`${API}/rts/${editingRecord.id}`, { status: "After-Sales" });
      
      toast.success("After-Sales case created! 🎉");
      setShowAfterSalesDialog(false);
      setEditingRecord(null);
      fetchRecords();
    } catch (error) {
      console.error("Error creating after-sales case:", error);
      toast.error("Failed to create After-Sales case");
    }
  };

  const handleSaveReorder = async () => {
    if (!editingRecord) return;
    
    if (reorderData.reorder_items.some(item => !item.item_name.trim())) {
      toast.error("Please fill in all item names");
      return;
    }

    try {
      const updateData = {
        status: "Nag Re-order",
        reorder_date: format(new Date(), "yyyy-MM-dd"),
        reorder_items: reorderData.reorder_items,
        reorder_notes: reorderData.reorder_notes,
        call_notes: reorderData.call_notes,
        call_date: format(new Date(), "yyyy-MM-dd")
      };
      
      await axios.put(`${API}/rts/${editingRecord.id}`, updateData);
      toast.success("Re-order saved successfully! 🎉");
      setShowReorderDialog(false);
      setEditingRecord(null);
      fetchRecords();
    } catch (error) {
      console.error("Error saving re-order:", error);
      toast.error("Failed to save re-order");
    }
  };

  const addReorderItem = () => {
    setReorderData({
      ...reorderData,
      reorder_items: [...reorderData.reorder_items, { item_name: "", quantity: 1 }]
    });
  };

  const removeReorderItem = (index) => {
    if (reorderData.reorder_items.length > 1) {
      setReorderData({
        ...reorderData,
        reorder_items: reorderData.reorder_items.filter((_, i) => i !== index)
      });
    }
  };

  const clearFilters = () => {
    setFilters({
      staff: "",
      reason: "",
      startDate: null,
      endDate: null,
    });
    setSearch("");
    setPlatformFilter("all");
  };

  const getStatusBadge = (status) => {
    const statusOption = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
    return <Badge className={statusOption.color}>{statusOption.label}</Badge>;
  };

  const getReasonColor = (reason) => {
    const lowerReason = reason?.toLowerCase() || "";
    if (lowerReason.includes("wrong")) return "bg-red-100 text-red-800";
    if (lowerReason.includes("damaged")) return "bg-orange-100 text-orange-800";
    if (lowerReason.includes("refused")) return "bg-yellow-100 text-yellow-800";
    if (lowerReason.includes("incomplete") || lowerReason.includes("wala")) return "bg-blue-100 text-blue-800";
    return "bg-gray-100 text-gray-800";
  };

  return (
    <div className="space-y-6" data-testid="rts-records-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">RTS Records</h1>
          <p className="text-gray-500 mt-1">View at i-manage ang lahat ng RTS entries</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={fetchRecords}
            variant="outline"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={exportToExcel}
            variant="outline"
            disabled={exporting || records.length === 0}
            className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
            data-testid="export-btn"
          >
            {exporting ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-4 h-4 mr-2" />
            )}
            Export Excel
          </Button>
          {/* Bulk Update Platform - Admin Only */}
          {user?.is_admin && (
            <Button 
              onClick={() => setShowBulkUpdateDialog(true)}
              variant="outline"
              className="bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
              data-testid="bulk-update-btn"
            >
              <Pencil className="w-4 h-4 mr-2" />
              Bulk Update Platform
            </Button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by tracking, name, or staff..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>

            {/* Platform Filter Dropdown */}
            <Select value={platformFilter} onValueChange={setPlatformFilter}>
              <SelectTrigger className="w-[160px]" data-testid="platform-filter">
                <SelectValue placeholder="All Platforms" />
              </SelectTrigger>
              <SelectContent>
                {PLATFORM_FILTER_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Filter Toggle */}
            <Button
              variant={showFilters ? "secondary" : "outline"}
              onClick={() => setShowFilters(!showFilters)}
              data-testid="filter-toggle-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {(filters.staff || filters.reason || filters.startDate || filters.endDate) && (
                <Badge className="ml-2 bg-red-600">Active</Badge>
              )}
            </Button>

            {/* Clear Filters */}
            {(search || platformFilter !== "all" || filters.staff || filters.reason || filters.startDate || filters.endDate) && (
              <Button
                variant="ghost"
                onClick={clearFilters}
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Clear
              </Button>
            )}
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 pt-4 border-t" data-testid="filter-section">
              <div>
                <Label>Staff</Label>
                <Input
                  placeholder="Filter by staff"
                  value={filters.staff}
                  onChange={(e) => setFilters({ ...filters, staff: e.target.value })}
                  data-testid="filter-staff"
                />
              </div>
              <div>
                <Label>Reason</Label>
                <Input
                  placeholder="Filter by reason"
                  value={filters.reason}
                  onChange={(e) => setFilters({ ...filters, reason: e.target.value })}
                  data-testid="filter-reason"
                />
              </div>
              <div>
                <Label>Start Date</Label>
                <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !filters.startDate && "text-muted-foreground"
                      )}
                      data-testid="filter-start-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {filters.startDate ? format(filters.startDate, "PPP") : "Pick date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={filters.startDate}
                      onSelect={(date) => {
                        setFilters({ ...filters, startDate: date });
                        setStartDateOpen(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>End Date</Label>
                <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !filters.endDate && "text-muted-foreground"
                      )}
                      data-testid="filter-end-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {filters.endDate ? format(filters.endDate, "PPP") : "Pick date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={filters.endDate}
                      onSelect={(date) => {
                        setFilters({ ...filters, endDate: date });
                        setEndDateOpen(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Records Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>All Records ({records.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12" data-testid="loading-spinner">
              <RefreshCw className="w-8 h-8 animate-spin text-red-600" />
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-12 text-gray-500" data-testid="no-records">
              <p>No RTS records found</p>
              <p className="text-sm mt-1">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="records-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tracking #</TableHead>
                    <TableHead>Date Created</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Platform</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map((record) => (
                    <TableRow key={record.id} data-testid={`record-row-${record.id}`}>
                      <TableCell className="font-mono text-sm">
                        {record.tracking_number}
                      </TableCell>
                      <TableCell className="text-sm">
                        <p className="font-medium">{record.created_at ? format(new Date(record.created_at), "MMM dd, yyyy") : "-"}</p>
                        <p className="text-xs text-gray-400">{record.created_at ? format(new Date(record.created_at), "hh:mm a") : ""}</p>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{record.customer_name}</p>
                        <p className="text-xs text-gray-500">{record.ordered_by}</p>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm">{record.contact_number || "-"}</p>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const plat = record.platform || "";
                          const branch = record.branch || "";
                          
                          // If platform already contains branch (like FB/J&T-QC), use it directly
                          if (plat.includes("-") && ["QC", "PQ", "CV", "PM", "VZ"].some(b => plat.endsWith(`-${b}`))) {
                            const basePlat = plat.split("-")[0];
                            return (
                              <Badge className={
                                basePlat === "Shopee" ? "bg-orange-100 text-orange-700 border border-orange-300" :
                                basePlat === "TikTok" ? "bg-gray-900 text-white" :
                                basePlat === "Lazada" ? "bg-purple-100 text-purple-700 border border-purple-300" :
                                "bg-blue-100 text-blue-700 border border-blue-300"
                              }>
                                {plat}
                              </Badge>
                            );
                          }
                          
                          // Build display from platform + branch
                          const isFBJT = !plat || plat === "JNT" || plat === "Facebook" || plat === "J&T Direct" || plat.includes("Facebook") || plat.includes("FB");
                          const basePlatform = isFBJT ? "FB/J&T" : (plat === "TikTok Shop" ? "TikTok" : plat);
                          
                          // ALWAYS show branch for FB/J&T
                          const displayBranch = branch || "PQ"; // Default to PQ for old records
                          const fullDisplay = isFBJT ? `${basePlatform}-${displayBranch}` : basePlatform;
                          
                          return (
                            <Badge className={
                              plat === "Shopee" ? "bg-orange-100 text-orange-700 border border-orange-300" :
                              plat === "TikTok" || plat === "TikTok Shop" ? "bg-gray-900 text-white" :
                              plat === "Lazada" ? "bg-purple-100 text-purple-700 border border-purple-300" :
                              "bg-blue-100 text-blue-700 border border-blue-300"
                            }>
                              {fullDisplay}
                            </Badge>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {record.orders?.map((order, i) => (
                            <span key={i} className="block">
                              {order.item_name} x{order.quantity}
                            </span>
                          ))}
                          {(!record.orders || record.orders.length === 0) && (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getReasonColor(record.reason)}>
                          {record.reason}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(record.status || "Pending")}
                        {record.status === "Nag Re-order" && record.reorder_date && (
                          <p className="text-xs text-green-600 mt-1">📅 {record.reorder_date}</p>
                        )}
                        {record.has_after_sales && (
                          <Badge className="bg-purple-100 text-purple-800 mt-1 text-xs">Has After-Sales</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(record)}>
                              <Edit2 className="w-4 h-4 mr-2" />
                              Edit Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleStatusChange(record, "Tinawagan")}>
                              <Phone className="w-4 h-4 mr-2" />
                              Mark as Tinawagan
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openReorderDialog(record)}>
                              <ShoppingCart className="w-4 h-4 mr-2 text-green-600" />
                              Record Re-order
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleStatusChange(record, "Hindi Na-contact")}>
                              <XCircle className="w-4 h-4 mr-2 text-yellow-600" />
                              Hindi Na-contact
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleStatusChange(record, "Ayaw Mag Re-order")}>
                              <XCircle className="w-4 h-4 mr-2 text-red-600" />
                              Ayaw Mag Re-order
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openAfterSalesDialog(record)}>
                              <Headphones className="w-4 h-4 mr-2 text-purple-600" />
                              Create After-Sales Case
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDelete(record.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit RTS Entry</DialogTitle>
          </DialogHeader>
          {editingRecord && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tracking Number</Label>
                  <Input
                    value={editingRecord.tracking_number}
                    onChange={(e) =>
                      setEditingRecord({ ...editingRecord, tracking_number: e.target.value })
                    }
                    className="font-mono"
                  />
                </div>
                <div>
                  <Label>Date Ordered</Label>
                  <Input
                    value={editingRecord.date_ordered}
                    onChange={(e) =>
                      setEditingRecord({ ...editingRecord, date_ordered: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Customer Name</Label>
                  <Input
                    value={editingRecord.customer_name}
                    onChange={(e) =>
                      setEditingRecord({ ...editingRecord, customer_name: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Contact Number</Label>
                  <Input
                    value={editingRecord.contact_number}
                    onChange={(e) =>
                      setEditingRecord({ ...editingRecord, contact_number: e.target.value })
                    }
                  />
                </div>
              </div>

              <div>
                <Label>Address</Label>
                <Textarea
                  value={editingRecord.address}
                  onChange={(e) =>
                    setEditingRecord({ ...editingRecord, address: e.target.value })
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Reason</Label>
                  <Input
                    value={editingRecord.reason}
                    onChange={(e) =>
                      setEditingRecord({ ...editingRecord, reason: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Cause Classification</Label>
                  <Input
                    value={editingRecord.cause_classification}
                    onChange={(e) =>
                      setEditingRecord({ ...editingRecord, cause_classification: e.target.value })
                    }
                  />
                </div>
              </div>

              {/* Platform Selection */}
              <div>
                <Label>Platform</Label>
                <Select 
                  value={editingRecord.platform || "FB/J&T-PQ"} 
                  onValueChange={(value) => {
                    // Extract branch from platform if it contains one
                    let branch = editingRecord.branch || "";
                    if (value.includes("-")) {
                      const parts = value.split("-");
                      branch = parts[parts.length - 1];
                    }
                    setEditingRecord({ ...editingRecord, platform: value, branch: branch });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Platform" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FB/J&T-QC">FB/J&T-QC (Quezon City)</SelectItem>
                    <SelectItem value="FB/J&T-PQ">FB/J&T-PQ (Parañaque)</SelectItem>
                    <SelectItem value="Shopee">Shopee</SelectItem>
                    <SelectItem value="TikTok">TikTok</SelectItem>
                    <SelectItem value="Lazada">Lazada</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={editingRecord.notes}
                  onChange={(e) =>
                    setEditingRecord({ ...editingRecord, notes: e.target.value })
                  }
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateRecord} className="bg-red-600 hover:bg-red-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Re-order Dialog */}
      <Dialog open={showReorderDialog} onOpenChange={setShowReorderDialog}>
        <DialogContent className="max-w-lg" data-testid="reorder-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-green-600" />
              Record Re-order
            </DialogTitle>
          </DialogHeader>
          {editingRecord && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-medium">{editingRecord.customer_name}</p>
                <p className="text-sm text-gray-500">{editingRecord.contact_number}</p>
                <p className="text-xs text-gray-400 mt-1">Tracking: {editingRecord.tracking_number}</p>
              </div>

              <div>
                <Label className="flex items-center justify-between mb-2">
                  <span>Re-order Items</span>
                  <Button type="button" variant="outline" size="sm" onClick={addReorderItem}>
                    <Plus className="w-3 h-3 mr-1" /> Add Item
                  </Button>
                </Label>
                <div className="space-y-2">
                  {reorderData.reorder_items.map((item, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <Input
                        placeholder="Item name"
                        value={item.item_name}
                        onChange={(e) => {
                          const newItems = [...reorderData.reorder_items];
                          newItems[index].item_name = e.target.value;
                          setReorderData({ ...reorderData, reorder_items: newItems });
                        }}
                        className="flex-1"
                      />
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => {
                          const newItems = [...reorderData.reorder_items];
                          newItems[index].quantity = parseInt(e.target.value) || 1;
                          setReorderData({ ...reorderData, reorder_items: newItems });
                        }}
                        className="w-20"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500"
                        onClick={() => removeReorderItem(index)}
                        disabled={reorderData.reorder_items.length === 1}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label>Call Notes</Label>
                <Textarea
                  placeholder="Notes from the call..."
                  value={reorderData.call_notes}
                  onChange={(e) => setReorderData({ ...reorderData, call_notes: e.target.value })}
                />
              </div>

              <div>
                <Label>Re-order Notes</Label>
                <Textarea
                  placeholder="Additional notes for re-order..."
                  value={reorderData.reorder_notes}
                  onChange={(e) => setReorderData({ ...reorderData, reorder_notes: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReorderDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveReorder} className="bg-green-600 hover:bg-green-700">
              <CheckCircle className="w-4 h-4 mr-2" />
              Save Re-order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* After-Sales Dialog */}
      <Dialog open={showAfterSalesDialog} onOpenChange={setShowAfterSalesDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Headphones className="w-5 h-5 text-purple-600" />
              Create After-Sales Case
            </DialogTitle>
          </DialogHeader>
          {editingRecord && (
            <div className="space-y-4">
              <div className="bg-purple-50 p-3 rounded-lg">
                <p className="font-medium">{editingRecord.customer_name}</p>
                <p className="text-sm text-gray-500">{editingRecord.contact_number}</p>
                <p className="text-xs text-gray-400 mt-1">Tracking: {editingRecord.tracking_number}</p>
                <p className="text-xs text-gray-400">
                  Items: {editingRecord.orders?.map(o => o.item_name).join(", ")}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Issue Type *</Label>
                  <Select 
                    value={afterSalesForm.issue_type} 
                    onValueChange={(v) => setAfterSalesForm({...afterSalesForm, issue_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select issue" />
                    </SelectTrigger>
                    <SelectContent>
                      {ISSUE_TYPES.map(i => (
                        <SelectItem key={i} value={i}>{i}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Action Taken *</Label>
                  <Select 
                    value={afterSalesForm.action_taken} 
                    onValueChange={(v) => setAfterSalesForm({...afterSalesForm, action_taken: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select action" />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTIONS.map(a => (
                        <SelectItem key={a} value={a}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Issue Description</Label>
                <Textarea
                  placeholder="Describe the issue..."
                  value={afterSalesForm.issue_description}
                  onChange={(e) => setAfterSalesForm({...afterSalesForm, issue_description: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>New Tracking # (if applicable)</Label>
                  <Input
                    value={afterSalesForm.new_tracking_number}
                    onChange={(e) => setAfterSalesForm({...afterSalesForm, new_tracking_number: e.target.value})}
                    placeholder="JNT..."
                    className="font-mono"
                  />
                </div>
                <div>
                  <Label>Staff In Charge *</Label>
                  <Input
                    value={afterSalesForm.staff_in_charge}
                    onChange={(e) => setAfterSalesForm({...afterSalesForm, staff_in_charge: e.target.value})}
                    placeholder="Staff name"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAfterSalesDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateAfterSales} className="bg-purple-600 hover:bg-purple-700">
              <Headphones className="w-4 h-4 mr-2" />
              Create Case
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Update Platform Dialog */}
      <Dialog open={showBulkUpdateDialog} onOpenChange={setShowBulkUpdateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-purple-600" />
              Bulk Update Platform
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Update all RTS records for a specific staff to a new platform.
            </p>
            
            <div className="space-y-2">
              <Label>Staff Name</Label>
              <Input
                placeholder="e.g., KC"
                value={bulkUpdateStaff}
                onChange={(e) => setBulkUpdateStaff(e.target.value)}
              />
              <p className="text-xs text-gray-500">All records by this staff will be updated</p>
            </div>
            
            <div className="space-y-2">
              <Label>New Platform</Label>
              <Select value={bulkUpdatePlatform} onValueChange={setBulkUpdatePlatform}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FB/J&T-QC">FB/J&T-QC</SelectItem>
                  <SelectItem value="FB/J&T-PQ">FB/J&T-PQ</SelectItem>
                  <SelectItem value="Shopee">Shopee</SelectItem>
                  <SelectItem value="TikTok">TikTok</SelectItem>
                  <SelectItem value="Lazada">Lazada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkUpdateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkUpdatePlatform}
              disabled={bulkUpdating || !bulkUpdateStaff.trim()}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {bulkUpdating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Updating...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" /> Update Records
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
